import React, { useEffect, useState } from "react"
import { Col, Form, Row } from "reactstrap"
import ReactQuill from "react-quill"

import FormSkeleton from "components/FormSkeleton/FormSkeleton"

import {
   GetWarrantyDetails,
   GetTrailersListing,
} from "../../../api/api.service"
import { Button } from "../../../components/Button"
import FormikErrorText from "../../../components/FormikErrorText/FormikErrorText"
import { Modal } from "../../../components/Modal"
import { TextInput } from "../../../components/TextInput"
import { AutoComplete } from "../../../components/AutoComplete"
import { ConfiguratorPackagesStatus, TrailerSize } from "../../../constants"
import ActiveInactiveSwitch from "../../Configurator/CommonUI/ActiveInactiveSwitch/ActiveInactiveSwitch"
import { FIELDS_NAME, TOGGLE_STATUS } from "./GlobalWarrantyFormConfig.js"
import useAsyncSelect from "../../../hooks/useAsyncSelect"
import autoCompleteStyles from "../../../components/AutoComplete/autoCompleteStyles"
import { makeTrailerName } from "utils/commonUtils"

const TITLES = {
   MODAL_ADD_TITLE: "Add Warranty",
   MODAL_UPDATE_TITLE: "Update Warranty",
   MODAL_DETAILS_TITLE: "Warranty Details",
   ADD_BTN_TEXT: "Save",
   UPDATE_BTN_TEXT: "Update",
   DETAILS_BTN_TEXT: "Done",
}

const handleStatusConversion = status => {
   return status == ConfiguratorPackagesStatus.Active ? true : false
}

const ManageGlobalWarranties = ({
   isDetailView = false,
   isOpen = false,
   onToggleModal = () => {},
   id = "",
   formik,
   isLoading = false,
}) => {
   const [status, setStatus] = useState(true)
   const [isFetching, setIsFetching] = useState(false)
   const [isDefault, setIsDefault] = useState(false)
   const [isPopular, setIsPopular] = useState(false)

   const {
      setSelectedOption: setSelectedTrailerType,
      selectEntity: trailerTypeOptions,
      setSelectEntity: setTrailerTypeOptions,
      handleSearchOnOptions: handleSearchTrailerType,
      selectedOption: selectedTrailerType,
   } = useAsyncSelect({
      apiFunc: GetTrailersListing,
      labelKey: "trailerType",
      valueKey: "id",
      params: { limit: -1 },
      modifyLabel: true,
      handleLabelModification: options => {
         return options.map(item => {
            const { description, ...rest } = item
            return {
               ...rest,
               label: makeTrailerName({
                  trailerType: item?.trailerType?.trailerType,
                  trailerModel: item?.model[0],
                  trailerSize: TrailerSize.getDisplayTextKey(
                     item?.configuratorTrailerSizeType[0]
                  ),
               }),
            }
         })
      },
   })

   useEffect(() => {
      if (isOpen && !id) {
         formik.resetForm()
         setSelectedTrailerType([])
         setStatus(true)
         setIsPopular(false)
      } else if (isOpen && id) {
         handleFetchDetails()
      }
   }, [id, isOpen])

   const handleFetchDetails = async () => {
      if (!id) return

      try {
         setIsFetching(true)
         let res = await GetWarrantyDetails(id)

         const trailerIds = res?.configuratorTrailerWarranties?.length
            ? [
                 ...new Set(
                    res.configuratorTrailerWarranties
                       .map(t => t.configuratorTrailerId)
                       .filter(Boolean)
                 ),
              ]
            : []

         let trailerTypesList = trailerTypeOptions?.data || []
         if (trailerTypesList.length === 0) {
            const trailersRes = await GetTrailersListing({ limit: -1 })
            const rawTrailerTypes = trailersRes?.data || []
            trailerTypesList = rawTrailerTypes.map(item => {
               const { description, ...rest } = item
               return {
                  ...rest,
                  label: makeTrailerName({
                     trailerType: item?.trailerType?.trailerType,
                     trailerModel: item?.model[0],
                     trailerSize: TrailerSize.getDisplayTextKey(
                        item?.configuratorTrailerSizeType[0]
                     ),
                  }),
                  value: item.id,
               }
            })
            setTrailerTypeOptions(prev => ({
               ...prev,
               data: trailerTypesList,
            }))
         }

         const trailerTypesData = trailerIds
            .map(trailerId => {
               const matchedType = trailerTypesList.find(
                  opt => opt.id === trailerId
               )
               return matchedType || null
            })
            .filter(Boolean)

         formik.setValues({
            [FIELDS_NAME.NAME]: res?.name,
            [FIELDS_NAME.PRICE]: res?.price,
            [FIELDS_NAME.STATUS]: res?.status,
            [FIELDS_NAME.YEAR]: res?.year,
            [FIELDS_NAME.DESCRIPTION]: res?.description,
            [FIELDS_NAME.DEFAULT]: res?.isDefault,
            [FIELDS_NAME.IS_POPULAR]: res?.isPopular,
            [FIELDS_NAME.TRAILER_TYPE]: trailerTypesData,
         })
         setIsDefault(res?.isDefault)
         setIsPopular(res?.isPopular)
         setStatus(handleStatusConversion(res?.status))
         setSelectedTrailerType(trailerTypesData)
      } finally {
         setIsFetching(false)
      }
   }

   const handleTrailerTypeChange = selectedOptions => {
      const options = selectedOptions || []
      setSelectedTrailerType(options)
      formik.setFieldValue(FIELDS_NAME.TRAILER_TYPE, options)
   }

   const handleModalClose = () => {
      setStatus(true)
      setIsPopular(false)
      setSelectedTrailerType([])
      formik.resetForm()
      onToggleModal()
   }

   const handleBooleanStatus = (evt, state, setState) => {
      let { name } = evt.target
      formik.setFieldValue(name, !state ? true : false)
      setState(!state)
   }
   const handleSetStatus = evt => {
      let { name } = evt.target
      let alteredStatus = !status
      setStatus(alteredStatus)
      formik.setFieldValue(
         name,
         alteredStatus
            ? ConfiguratorPackagesStatus.Active
            : ConfiguratorPackagesStatus.Inactive
      )
   }
   const handleSubmitForm = e => {
      e.preventDefault()
      return formik.handleSubmit()
   }

   return (
      <Modal
         isOpen={isOpen}
         handleModalToggling={handleModalClose}
         customButton={true}
         hideModalHeaderSeparator={true}
         headerClasses="header-container justify-content-center mb-0"
         sizeClasses="5"
         backdrop="static"
         modalTitle={id ? TITLES.MODAL_UPDATE_TITLE : TITLES.MODAL_ADD_TITLE}
      >
         {isFetching ? (
            <div>
               <FormSkeleton />
            </div>
         ) : (
            <div style={{ pointerEvents: isLoading ? "none" : "auto" }}>
               <Row>
                  <Col xs={12}>
                     <Form onSubmit={handleSubmitForm}>
                        <Row className="mb-3 align-items-center">
                           <label className=" col-md-3 col-form-label clr-theme-primary">
                              Warranty
                           </label>
                           <div className="col-md-9">
                              <TextInput
                                 inputClass={isDetailView && "input-readonly"}
                                 placeholder="Enter name"
                                 name={FIELDS_NAME.NAME}
                                 {...formik.getFieldProps(FIELDS_NAME.NAME)}
                              />
                              <FormikErrorText
                                 formikInstance={formik}
                                 fieldName={FIELDS_NAME.NAME}
                              />
                           </div>
                        </Row>

                        <Row className="mb-3 align-items-center">
                           <label className="col-md-3 col-form-label clr-theme-primary">
                              Configurator Trailers
                           </label>
                           <div className="col-md-9">
                              <AutoComplete
                                 isDisabled={isDetailView}
                                 handleBlur={() =>
                                    setTrailerTypeOptions(prev => ({
                                       ...prev,
                                       page: 1,
                                       Q: "",
                                    }))
                                 }
                                 onChange={handleTrailerTypeChange}
                                 onInputChange={handleSearchTrailerType}
                                 placeholder="Select Trailer Type"
                                 classNamePrefix="add-new-req"
                                 customStyles={autoCompleteStyles.STYLE_FOUR}
                                 options={trailerTypeOptions?.data}
                                 value={selectedTrailerType}
                                 isLoading={trailerTypeOptions?.fetching}
                                 name={FIELDS_NAME.TRAILER_TYPE}
                                 isMulti={true}
                                 closeMenuOnSelect={false}
                                 menuPortalTarget={document.body}
                                 menuPosition="fixed"
                                 menuPlacement="auto"
                              />
                              <FormikErrorText
                                 formikInstance={formik}
                                 fieldName={FIELDS_NAME.TRAILER_TYPE}
                              />
                           </div>
                        </Row>

                        <Row className="mt-4 mb-3 align-items-center">
                           <label className=" col-md-3 col-form-label clr-theme-primary">
                              No. of Years
                           </label>
                           <div className="col-md-9">
                              <TextInput
                                 min={0}
                                 type="number"
                                 step="0.01"
                                 inputClass={isDetailView && "input-readonly"}
                                 placeholder="Enter no. of years"
                                 name={FIELDS_NAME.YEAR}
                                 {...formik.getFieldProps(FIELDS_NAME.YEAR)}
                              />

                              <FormikErrorText
                                 formikInstance={formik}
                                 fieldName={FIELDS_NAME.YEAR}
                              />
                           </div>
                        </Row>

                        <Row className="mt-4 mb-3 align-items-center">
                           <label className=" col-md-3 col-form-label clr-theme-primary">
                              Price ($)
                           </label>
                           <div className="col-md-9">
                              <TextInput
                                 min={0}
                                 type="number"
                                 step="0.01"
                                 inputClass={isDetailView && "input-readonly"}
                                 placeholder="Enter price"
                                 name={FIELDS_NAME.PRICE}
                                 {...formik.getFieldProps(FIELDS_NAME.PRICE)}
                              />

                              <FormikErrorText
                                 formikInstance={formik}
                                 fieldName={FIELDS_NAME.PRICE}
                              />
                           </div>
                        </Row>

                        <Row className="mb-3 align-items-center">
                           <label className=" col-md-3 col-form-label clr-theme-primary ">
                              Description
                           </label>
                           <div className="col-md-9">
                              {!isDetailView && (
                                 <ReactQuill
                                    className="quill-description-write"
                                    theme="snow"
                                    placeholder="Enter item description"
                                    value={
                                       formik.values[FIELDS_NAME.DESCRIPTION]
                                    }
                                    onChange={value =>
                                       formik.setFieldValue(
                                          FIELDS_NAME.DESCRIPTION,
                                          value
                                       )
                                    }
                                 />
                              )}

                              <FormikErrorText
                                 formikInstance={formik}
                                 fieldName={FIELDS_NAME.DESCRIPTION}
                              />
                           </div>
                        </Row>
                        <Row className="mb-3 align-items-center">
                           <label className=" col-md-3 col-form-label clr-theme-primary">
                              Popular Warranty
                           </label>
                           <div className="col-md-9">
                              <ActiveInactiveSwitch
                                 isChecked={isPopular}
                                 isDisable={isDetailView}
                                 name={FIELDS_NAME.IS_POPULAR}
                                 onChange={evt =>
                                    handleBooleanStatus(
                                       evt,
                                       isPopular,
                                       setIsPopular
                                    )
                                 }
                                 activeText={TOGGLE_STATUS.Yes}
                                 inActiveText={TOGGLE_STATUS.No}
                              />
                              <FormikErrorText
                                 formikInstance={formik}
                                 fieldName={FIELDS_NAME.IS_POPULAR}
                              />
                           </div>
                        </Row>

                        <Row className="mb-3 align-items-center">
                           <label className=" col-md-3 col-form-label clr-theme-primary">
                              Status
                           </label>
                           <div className="col-md-9">
                              <ActiveInactiveSwitch
                                 isChecked={status}
                                 isDisable={isDetailView}
                                 name={FIELDS_NAME.STATUS}
                                 onChange={handleSetStatus}
                              />
                              <FormikErrorText
                                 formikInstance={formik}
                                 fieldName={FIELDS_NAME.STATUS}
                              />
                           </div>
                        </Row>
                        <Row className="mb-3 align-items-center">
                           <label className=" col-md-3 col-form-label clr-theme-primary">
                              Default
                           </label>
                           <div className="col-md-9">
                              <ActiveInactiveSwitch
                                 isChecked={isDefault}
                                 isDisable={isDetailView}
                                 name={FIELDS_NAME.DEFAULT}
                                 onChange={evt =>
                                    handleBooleanStatus(
                                       evt,
                                       isDefault,
                                       setIsDefault
                                    )
                                 }
                                 activeText={TOGGLE_STATUS.Yes}
                                 inActiveText={TOGGLE_STATUS.No}
                              />
                              <FormikErrorText
                                 formikInstance={formik}
                                 fieldName={FIELDS_NAME.DEFAULT}
                              />
                           </div>
                        </Row>

                        <div className="d-flex justify-content-center mt-3 mt-2">
                           <Button
                              isLoading={isLoading}
                              type={"submit"}
                              title={
                                 id
                                    ? TITLES.UPDATE_BTN_TEXT
                                    : TITLES.ADD_BTN_TEXT
                              }
                              className="header-button"
                              onClick={isDetailView && handleModalClose}
                           />
                        </div>
                     </Form>
                  </Col>
               </Row>
            </div>
         )}
      </Modal>
   )
}

export default ManageGlobalWarranties
